import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatearMoneda } from "../../config/moneda";
import { ESTADOS_ARCA, ESTADOS_VENTA } from "../../servicios/facturacionElectronica";
import { useStorePos } from "./useStorePos";

const PosResultadoFacturacion = () => {
  const { t } = useTranslation();
  const [verDetalle, setVerDetalle] = useState(false);
  const resultado = useStorePos((state) => state.ResultadoVenta);
  const estadoArca = useStorePos((state) => state.EstadoArca);
  const errorFiscal = useStorePos((state) => state.ErrorFiscal);
  const reintentando = useStorePos((state) => state.FacturasReintentando);
  const descargando = useStorePos((state) => state.DescargandoComprobante);
  const imprimiendo = useStorePos((state) => state.ImprimiendoComprobante);
  const confirmando = useStorePos((state) => state.ConfirmandoVenta);

  if (!resultado) return null;

  const facturaId = Number(resultado.facturaId) || 0;
  const autorizada = estadoArca === ESTADOS_ARCA.AUTORIZADA;
  const conErrorArca = estadoArca === ESTADOS_ARCA.ERROR;
  const estaReintentando = reintentando.includes(facturaId);
  const estadoVenta = resultado.estadoVenta || t("pos.statusNotReported");
  const pendienteIva = resultado.ivaPendiente || estadoVenta === ESTADOS_VENTA.PENDIENTE_IVA;
  const pendienteFacturacion = estadoVenta === ESTADOS_VENTA.PENDIENTE_FACTURACION;
  const errorFacturaLocal = estadoVenta === ESTADOS_VENTA.ERROR_FACTURA_LOCAL || estadoArca === ESTADOS_ARCA.ERROR_LOCAL;
  const facturada = estadoVenta === ESTADOS_VENTA.FACTURADA || facturaId > 0;

  return (
    <section className="pos-resultado" aria-live="polite">
      <div className="pos-resultado-header">
        <div>
          <span className="pos-resultado-kicker">{facturada ? t("pos.invoicedSale") : pendienteIva ? t("pos.saleRegisteredInvoiceWaiting") : t("pos.saleAndDeliveryGenerated")}</span>
          <h4>{t("pos.saleTitle", { number: resultado.ventaId })}</h4>
        </div>
        <span className={`pos-estado-fiscal ${estadoVenta || "sin-factura"}`}>
          {estadoVenta}
        </span>
      </div>

      <div className="pos-resultado-datos">
        <div><span>{t("pos.deliveryNote")}</span><strong>{resultado.remitoNumero || resultado.remitoId || t("pos.pendingValue")}</strong></div>
        <div><span>{t("pos.invoice")}</span><strong>{resultado.facturaNumero || facturaId || t("pos.pendingValue")}</strong></div>
        <div><span>{t("common.total")}</span><strong>{formatearMoneda(resultado.venta?.oResumen?.TotalConIva ?? resultado.venta?.oResumen?.Total)}</strong></div>
        <div><span>{t("pos.paymentMethod")}</span><strong>{resultado.venta?.FormaPago || "-"}</strong></div>
      </div>

      {errorFiscal && <p className="pos-resultado-error">{errorFiscal}</p>}
      {pendienteIva && (
        <div className="pos-resultado-iva-pendiente" role="status">
          <i className="pi pi-clock" aria-hidden="true" />
          <div>
            <strong>{t("pos.newProductVatMissing")}</strong>
            <p>{t("pos.newProductVatMissingHelp")}</p>
          </div>
        </div>
      )}
      {pendienteFacturacion && <p>{t("pos.invoiceStillPending")}</p>}
      {errorFacturaLocal && <p>{t("pos.localInvoiceRetryHelp")}</p>}

      <div className="pos-resultado-acciones">
        <button type="button" className="pos-btn-secundario" onClick={() => useStorePos.getState().nuevaVenta()}>
          <i className="pi pi-plus" /> {t("pos.newSale")}
        </button>
        <button type="button" className="pos-btn-secundario" onClick={() => setVerDetalle((actual) => !actual)}>
          <i className="pi pi-eye" /> {verDetalle ? t("pos.hideDetails") : t("pos.viewDetails")}
        </button>
        {facturaId > 0 && (
          <button
            type="button"
            className="pos-btn-principal"
            onClick={() => useStorePos.setState({ ComprobacionFiscalVisible: true, ComprobacionFiscal: null })}
          >
            <i className="pi pi-search" /> {t("pos.viewFiscalCheck")}
          </button>
        )}
        {!pendienteIva && (pendienteFacturacion || errorFacturaLocal) && facturaId === 0 && (
          <button
            type="button"
            className="pos-btn-alerta"
            disabled={confirmando}
            onClick={() => useStorePos.getState().reintentarVentaPendiente()}
          >
            <i className="pi pi-refresh" /> {confirmando ? t("pos.retrying") : t("pos.retryInvoicing")}
          </button>
        )}
        {(pendienteIva || pendienteFacturacion || errorFacturaLocal) && (
          <button type="button" className="pos-btn-secundario" onClick={() => useStorePos.getState().recuperarVentaPendiente()}>
            <i className="pi pi-sync" /> {t("pos.updateStatus")}
          </button>
        )}
        {conErrorArca && facturaId > 0 && (
          <button
            type="button"
            className="pos-btn-alerta"
            disabled={estaReintentando}
            onClick={() => useStorePos.getState().reintentarArca(facturaId)}
          >
            <i className="pi pi-refresh" /> {estaReintentando ? t("pos.retrying") : t("pos.retryArcaAction")}
          </button>
        )}
        {autorizada && (
          <>
            <button type="button" className="pos-btn-secundario" disabled={imprimiendo} onClick={() => useStorePos.getState().imprimirTicket(facturaId)}>
              <i className="pi pi-print" /> {t("pos.printTicket")}
            </button>
            <button type="button" className="pos-btn-principal" disabled={descargando} onClick={() => useStorePos.getState().descargarA4(facturaId)}>
              <i className="pi pi-download" /> {t("pos.downloadA4")}
            </button>
          </>
        )}
      </div>

      {verDetalle && (
        <div className="pos-resultado-detalle">
          <p>{t("pos.commercialStatus")}: <strong>{resultado.estadoVenta || t("pos.confirmed")}</strong></p>
          <p>{t("pos.dispatch")}: <strong>{resultado.despachoId || "-"}</strong></p>
          <p>{t("pos.lines")}: <strong>{resultado.venta?.lstDetalle?.length || 0}</strong></p>
          <p>{t("pos.change")}: <strong>{formatearMoneda(resultado.venta?.Vuelto)}</strong></p>
        </div>
      )}
    </section>
  );
};

export default PosResultadoFacturacion;
